<template>
  <div class="data-table">
    <div class="data-table__header">
      <div class="header-row row">
        <div
          class="cell cell__column cell__padding cell__column--first"
          @click="selectCell(0, 0)"
        ></div>
        <div
          v-for="(column, cellColumnOrder) in columns"
          :key="`HEADER_CELL_${column}_${cellColumnOrder}`"
          class="cell cell__column cell__padding"
          :class="[
            { 'cell--highlight': cellColumnOrder === selected.columnOrder },
          ]"
          @click="selectCell(cellColumnOrder, (selected.rowOrder || 0))"
        >
          {{ column }}
          <!-- <template v-if="cellColumnOrder === 0">
            Default Text
          </template>
          <template v-else>
            Variable Line {{ cellColumnOrder }}
          </template> -->
        </div>
      </div>
    </div>
    <div class="data-table__body">
      <div
        class="body-row row"
        v-for="(rowObject, rowOrder) in rows"
        :key="`BODY_ROW_${rowOrder}_${rowObject.key}`"
      >
        <div
          class="cell cell__row cell__row--first"
          :class="[
            { 'cell--highlight': rowOrder === selected.rowOrder },
          ]"
          @click="selectCell((selected.columnOrder || 0), rowOrder)"
        >
          <span
            class="cell__padding"
          >
            
            {{ rowOrder + 1 }}
          </span>
        </div>

        <div
          class="cell cell__row"
          v-for="(column, columnOrder) in columns"
          :key="`BODY_CELL_${column}_${columnOrder}_${rowObject.key}`"
        >
          <div
            class="selectable-cell selectable-cell__margin"
            :class="[
              { 'selectable-cell--selected': selected.rowKey === rowObject.key && selected.columnOrder === columnOrder },
            ]"
            @click="selectCell(columnOrder, rowOrder)"
          >
            <input
              :name="`cell-input-${rowObject.key}`"
              type="text"
              class="cell-input"
              :value="(model[rowObject.key]) ? model[rowObject.key][columnOrder] : ''"
              @click="selectCell(columnOrder, rowOrder)"
              @input="(event) => updateModel(rowObject.key, columnOrder, event)"
            ></input>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect, type Ref } from 'vue';
import ExecutionDelay from 'execution-delay';
import type { IRow } from '../../types/table.types';
import { E_DATA_TABLE_EVENT } from '../../types/components-events.enums';
import type { TDataTableEventResponse } from '../../types/event-response.types';
import { useLineItemStore } from '../../stores';
import pinia from '../../core/pinia';
import { E_SCENARIO } from '../../types/line-item.types';

interface IProps {
  columns: string[];
  rows: IRow[];
  surfaceIndex: number;
}
const props = defineProps<IProps>();
const emit = defineEmits<{
  (event: E_DATA_TABLE_EVENT.UPDATE_MODEL, payload: TDataTableEventResponse[E_DATA_TABLE_EVENT.UPDATE_MODEL]): void;
  (event: E_DATA_TABLE_EVENT.UPDATE_SELECTED_ROW_KEY, payload: TDataTableEventResponse[E_DATA_TABLE_EVENT.UPDATE_SELECTED_ROW_KEY]): void;
}>();

const lineItemStore = useLineItemStore(pinia);

const model: Ref<Record<string, Record<number, any>>> = ref(getModel());

watchEffect(() => {
  const newValue = getModel(props.rows);
  if (model.value !== newValue) {
    model.value = newValue;
  }
});

const selected: Ref<{
  rowKey: string | null;
  columnOrder: number | null;
  rowOrder: number | null;
}> = ref({
  rowKey: null,
  columnOrder: null,
  rowOrder: null,
});

/*
* Select all entered text when focusing on a cell.
* TODO: Uncomment if needed.
*/
// function focusCell(event: any) {
  // const targetValue: string = event.target.value;
  // event.target.selectionStart = 0;
  // event.target.selectionEnd = targetValue.length;
// }

function selectCell(columnOrder: number, rowOrder: number) {
  const rowKey = props.rows[rowOrder].key;
  if (selected.value.rowKey === rowKey && selected.value.columnOrder === columnOrder) {
    return;
  }
  selected.value.rowKey = rowKey;
  selected.value.columnOrder = columnOrder;
  selected.value.rowOrder = rowOrder;
  emit(E_DATA_TABLE_EVENT.UPDATE_SELECTED_ROW_KEY, {
    rowKey: rowKey,
    surfaceIndex: props.surfaceIndex,
  });
}

function updateModel(rowKey: string, columnOrder: number, event: any) {
  const targetValue: string = event.target.value;
  if (model.value[rowKey]) {
    model.value[rowKey][columnOrder] = targetValue;
  } else {
    model.value[rowKey] = Object.fromEntries(props.columns.map((_, index) => {
      if (index === columnOrder) {
        return [index, targetValue];
      } else {
        return [index, ''];
      }
    }));
  }
  lineItemStore.updateLineItem({
    scenario: E_SCENARIO.VDP,
  });
  ExecutionDelay.add(E_DATA_TABLE_EVENT.UPDATE_MODEL, () => {
    emit(E_DATA_TABLE_EVENT.UPDATE_MODEL, {
      model: model.value,
      surfaceIndex: props.surfaceIndex,
    });
  }, 700);
}

function getModel(proxyRow?: IRow[]): Record<string, Record<number, any>> {
  const rows: IRow[] = JSON.parse(JSON.stringify(proxyRow || props.rows));
  const columns: string[] = JSON.parse(JSON.stringify(props.columns));
  const result: Record<string, Record<number, any>> = {};
  rows.forEach((rowObject) => {
    if (!result[rowObject.key]) {
      result[rowObject.key] = {};
    }
    result[rowObject.key] = Object.fromEntries(columns.map((_, index) => {
      return [index, rowObject.row[columns[index]]];
    }));
  });
  return result;
}
</script>

<style scoped lang="scss">
.data-table {
  position: relative;
  width: 100%;
  border-right: 1px solid #E0E0E0;
  border-bottom: 1px solid #E0E0E0;
  overflow: auto;
  max-height: calc($row-height*10);

  &__header,
  &__body {
    display: flex;
    flex-direction: column;
  }

  &__header {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 100;
  }

  &::-webkit-scrollbar-track {
    background-color: #F5F5F5;
  }

  &::-webkit-scrollbar {
    width: 3px;
    background-color: #F5F5F5;
  }

  &::-webkit-scrollbar-thumb {
    background-color: $brand-color;
  }
}

.row {
  display: grid;
  grid-template-columns: 60px; 
  grid-auto-columns: 1fr;   
  grid-auto-flow: column;
  height: $row-height;
}

.cell {
  border: 1px solid #E0E0E0;
  border-right: none;
  border-bottom: none;
  text-align: left;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  vertical-align: middle;
  color: #282828;
  transition: background 0.2s ease-in-out;

  &__column {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #F0F0F0;
    user-select: none;
    cursor: pointer;
  }

  &__row {
    background: #FFF;
    
    &--first {
      user-select: none;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #F0F0F0;
      cursor: pointer;

      & span {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  &__padding {
    line-height: normal;
    padding: 4px 8px;
  }

  &--highlight {
    background: #dcdcdc;
  }
}

.selectable-cell {
  &--selected {
    outline: 2px solid $brand-color;
  }

  &__margin {
    display: flex;
    line-height: normal;
    margin: 2px;
  }
}

.cell-input {
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: none;
  display: flex;
  align-items: center;
  padding: 2px 6px;
  margin: 0;
  text-align: left;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  vertical-align: middle;
  color: #282828;
}
</style>